import { trackEvent } from "./amplitudeTracker";

const cookieDomain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN as string;

const FIRST_TOUCH_COOKIE = "m8l-first-touch";
const LAST_TOUCH_COOKIE = "m8l-last-touch";
const URL_TEMP_COOKIE_NAME = "m8l-urls-temp";
const PATH_HISTORY_COOKIE = "m8l-path-history";

const UTM_PARAMS_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const SEARCH_ENGINES = ["google.com", "bing.com", "yahoo.com", "duckduckgo.com", "ask.com", "yandex.com", "baidu.com"] as const;
const LLM_DOMAINS = ["chatgpt.com", "perplexity.ai", "claude.ai", "grok.com", "gemini.google.com", "deepseek.com"] as const;

type UtmKey = (typeof UTM_PARAMS_KEYS)[number];
type UtmParams = Partial<Record<UtmKey, string>>;

export const getCookies = (name: string) => {
    try {
        const cookieArray = document.cookie.split(";");

        const cookieName = `${name}=`;

        for (let cookie of cookieArray) {
            cookie = cookie.trim();
            if (cookie.indexOf(cookieName) === 0) {
                const cookieParsedValue = JSON.parse(cookie.split("=")[1]);
                return cookieParsedValue;
            }
        }
        return null;
    } catch (e) {
        return null;
    }
};

const setCookieStorage = (name: string, value: any, expirationDays?: number) => {
    const expireData = new Date(new Date().getTime() + (expirationDays ?? 30) * 60 * 24 * 60 * 1000);

    if (!cookieDomain) {
        console.log("Cookie domain is not set");
        return;
    }
    document.cookie = `${name}=${JSON.stringify(value)};expires=${expireData};domain=${cookieDomain};path=/`;
};

export const getUtmParamsFromUrl = (): UtmParams | null => {
    try {
        const search = window.location.search;
        const params = new URLSearchParams(search);

        const result: UtmParams = {};

        for (const key of UTM_PARAMS_KEYS) {
            const value = params.get(key);
            if (value) {
                result[key] = value;
            }
        }

        return Object.keys(result).length > 0 ? result : null;
    } catch (e) {
        return null;
    }
};

const getDomain = (url: string): string | null => {
    try {
        return new URL(url).hostname;
    } catch (e) {
        return null;
    }
};

const getCurrentUrl = () => {
    return window.location.href;
};

function isFromSearchEngine() {
    if (!document.referrer) return false;
    const referrerDomain = getDomain(document.referrer);
    if (!referrerDomain) return false;
    return SEARCH_ENGINES.some((engine) => referrerDomain.includes(engine));
}

function isFromLLM() {
    if (!document.referrer) return false;
    const referrerHostname = getDomain(document.referrer);
    if (!referrerHostname) return false;
    return LLM_DOMAINS.some((llm) => referrerHostname.includes(llm));
}

function isNewSession() {
    if (getCookies(URL_TEMP_COOKIE_NAME)) {
        return false;
    } else {
        setCookieStorage(URL_TEMP_COOKIE_NAME, "1");
        return true;
    }
}

function getTrafficData() {
    try {
        const urlUtms = getUtmParamsFromUrl();
        const referrerUrl = document.referrer;
        const referrerHostname = referrerUrl ? getDomain(referrerUrl) : null;
        const currentLandingPageUrl = getCurrentUrl();
        const currentSiteHostname = window.location.hostname;

        const isInternalNavigation = referrerHostname === currentSiteHostname;

        if (isInternalNavigation) {
            const lastTouch = getCookies(LAST_TOUCH_COOKIE);
            if (lastTouch) {
                return {
                    ...lastTouch,
                    current_page: currentLandingPageUrl,
                    previous_page: referrerUrl,
                    is_internal_navigation: true,
                };
            }
        }

        const trackingData = {
            landing_page: currentLandingPageUrl,
            referrer: referrerUrl || "none",
            is_internal_navigation: false,
        };

        if (urlUtms && Object.keys(urlUtms).length > 0) {
            const utmData = {
                utm_source: urlUtms.utm_source || "(not-set)",
                utm_medium: urlUtms.utm_medium || "(not-set)",
                utm_campaign: urlUtms.utm_campaign || "(not-set)",
                utm_term: urlUtms.utm_term || "(not-set)",
                utm_content: urlUtms.utm_content || "(not-set)",
            };
            return { ...trackingData, ...utmData };
        }

        if (isFromSearchEngine()) {
            const searchEngine = SEARCH_ENGINES.find((engine) => referrerHostname && referrerHostname.includes(engine));
            return {
                ...trackingData,
                utm_source: searchEngine || "other_search",
                utm_medium: "organic",
                traffic_type: "organic",
            };
        }

        if (isFromLLM()) {
            const llmSource = LLM_DOMAINS.find((llm) => referrerHostname && referrerHostname.includes(llm)) || "unknown_llm";
            return {
                ...trackingData,
                utm_source: llmSource,
                utm_medium: "llm",
                traffic_type: "llm",
            };
        }

        if (!referrerHostname) {
            return {
                ...trackingData,
                utm_source: "direct",
                utm_medium: "none",
                traffic_type: "direct",
            };
        }

        return {
            ...trackingData,
            utm_source: referrerHostname,
            utm_medium: "referral",
            traffic_type: "referral",
        };
    } catch (e) {
        console.log("Error getting traffic data:", e);
        return null;
    }
}

export const firstAndLastTouchTracker = () => {
    try {
        const trafficData = getTrafficData();
        if (!trafficData) return;

        const firstTouch = getCookies(FIRST_TOUCH_COOKIE);
        if (!firstTouch) {
            setCookieStorage(
                FIRST_TOUCH_COOKIE,
                {
                    ...trafficData,
                    touch_type: "first_touch",
                    entry_point: trafficData.landing_page,
                },
                365
            );
        }

        const isNewUserSession = isNewSession();
        const lastTouch = getCookies(LAST_TOUCH_COOKIE);

        const updatedLastTouch = {
            ...lastTouch,
            ...trafficData,
            touch_type: "last_touch",
            is_new_session: isNewUserSession,
            entry_point: trafficData.landing_page,
        };

        setCookieStorage(LAST_TOUCH_COOKIE, updatedLastTouch, 30);
    } catch (e) {
        console.log("Error updating touch points:", e);
    }
};

export const multipleTouchPointTracker = () => {
    try {
        const currentUrl = getCurrentUrl();
        const pathHistory = getCookies(PATH_HISTORY_COOKIE) || [];

        const MAX_HISTORY = 25;

        if (pathHistory.length === 0 || pathHistory[pathHistory.length - 1] !== currentUrl) {
            pathHistory.push(currentUrl);

            const trimmedHistory = pathHistory.slice(-MAX_HISTORY);

            setCookieStorage(PATH_HISTORY_COOKIE, trimmedHistory, 30 * 24 * 60);
        }
    } catch (e) {
        console.warn("Error tracking page view:", e);
    }
};

export const utmTracker = () => {
    firstAndLastTouchTracker();
    multipleTouchPointTracker();

    const firstTouch = getCookies(FIRST_TOUCH_COOKIE);
    const lastTouch = getCookies(LAST_TOUCH_COOKIE);
    const pathHistory = getCookies(PATH_HISTORY_COOKIE);

    trackEvent({ event: "utm tracker", properties: { firstTouch, lastTouch, pathHistory } });
};
